/* eslint-disable react/jsx-one-expression-per-line */
import { Formik } from 'formik';
import * as yup from 'yup';
import React from 'react';
import { StatusCodes } from 'http-status-codes';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import FileInputComponent from '../../../../components/common/forms/FileInput/FileInputComponent';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { UPLOAD_MAX_SIZE_IMAGE, FILE_UPLOAD_TYPES } from '../../../../utils/constants';
import FilesApi from '../../../../services/api/FilesApi';
import ProductImportsApi from '../../../../services/api/ProductImportsApi';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import ImportResults from '../../../imports/components/ResultDtoDisplay';
import styled from 'styled-components';
import DataOperationsNav from '~/src/components/common/navigation/DataOperationsNav';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import { Colors } from '~/src/scss/common/style';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import exportIcon from '~/src/static/img/export.svg';

const validationSchema = yup.object().shape({
  certificate: yup.mixed().test('fileSize', 'Моля използвайте файлове до 3MB', (value) => {
    if (typeof value === 'undefined' || value.length <= 0) {
      return true;
    }

    return value[0].size <= UPLOAD_MAX_SIZE_IMAGE;
  }),
});

export default function UploadForImport(): JSX.Element {
  const [result, setResult] = React.useState(null);
  const [isExporting, setIsExporting] = React.useState(false);
  const history = useHistory();

  const onSubmitMine = async (values, setSubmitting) => {
    setSubmitting(true);
    const files = values.xlsx;

    if (files && files.length > 0) {
      const fileData = { files, type: FILE_UPLOAD_TYPES.PRODUCT_IMPORTS };
      var fileUploadResp = await FilesApi.create(fileData);
      
      if (fileUploadResp.status === StatusCodes.OK) {
        var res = await ProductImportsApi.importAnUploadSizes(fileUploadResp.data[0].id);

        if (res.status === StatusCodes.OK) {
          setResult(res.data);
        }
      }
    }
    setSubmitting(false);
  };

  const onExportSizes = async () => {
    setIsExporting(true);
    const x = await ProductImportsApi.exportSizes();
    setIsExporting(false);
    const courseSheet = x.data;
    
    if (courseSheet) {
      const blob = new Blob([courseSheet], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `export-sizes.xlsx`);
    }
  };

  return (
    <WhitePad>
      <div style={{ display: 'flex' }}>
        <DataOperationsNav />
        <div>
          <TitleComponent title="Product Sizes" 
           titleLeft="Back"
           onLeftHandler={(): void => history.goBack()}
           />
          <FormWrapper>
            <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
              <Title>Export Sizes </Title>
              <SecondaryButton
                icon={exportIcon}
                onClick={onExportSizes}
                size="sm"
                isSubmitting={isExporting}
                title="Export"
              />
            </div>
          </FormWrapper>
          <FormWrapper>
            <div>
              <Title>Instructions</Title>
              <StyleP>
                This import updates sizes and data of each packaging. Note that it does not create products, change
                their name or Gtin.
                <br />
                Upload an excel with the following columns in this specific order:
                <br />
              </StyleP>
              <TableTitle>Those Fields are used just for finding the correct products:</TableTitle>
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>CuGtin</b>- The Barcode of the individual unit.
                    </StyleTd>
                    <StyleTd>
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuGtin</b> - The Barcode of the Distribution unit.
                    </StyleTd>
                    <StyleTd>
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpGtin</b> - The Barcode of the display.
                    </StyleTd>
                    <StyleTd>
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>Name</b> - Name of the product.
                    </StyleTd>
                    <StyleTd>
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                </tbody>
              </Table>
              <TableTitle>Those Fields are used for updating information:</TableTitle>
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>CuWeightUnit</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuWidth</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuLength</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuHeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuNetWeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuGrossWeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuMaxLabelWidth</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuMaxLabelHeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CuWeightCapacity</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                </tbody>
              </Table>
              <TableTitle></TableTitle>
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>DuWeightUnit</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuWidth</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuLength</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuHeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuNetWeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuGrossWeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuMaxLabelWidth</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuMaxLabelHeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuWeightCapacity</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                </tbody>
              </Table>
              <TableTitle></TableTitle>
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>DpWeightUnit</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpWidth</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpLength</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpHeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpNetWeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpGrossWeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpMaxLabelWidth</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpMaxLabelHeight</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpWeightCapacity</b>
                    </StyleTd>
                    <StyleTd>Self Explanatory</StyleTd>
                  </tr>
                </tbody>
              </Table>
              <TableTitle>Those Fields are used for Options:</TableTitle>
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>DeletePreviousSizingInfo</b> (yes/no)
                    </StyleTd>
                    <StyleTd>This will wipe the entire data and start over</StyleTd>
                  </tr>
                </tbody>
              </Table>
              <Formik
                initialValues={{
                  xlsx: [],
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => onSubmitMine(values, setSubmitting)}
              >
                {({ values, handleChange, handleBlur, isSubmitting, setFieldValue, onSubmit, handleSubmit }) => (
                  <form autoComplete="off" onSubmit={handleSubmit}>
                    <FormRow>
                      <FileInputComponent
                        name="xlsx"
                        value={values.xlsx}
                        onBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        cols={12}
                      />
                    </FormRow>
                    <PrimaryButton variant="primary" type="submit" title="Upload" isSubmitting={isSubmitting} />
                  </form>
                )}
              </Formik>
            </div>
          </FormWrapper>
          <ImportResults result={result} />
        </div>
      </div>
    </WhitePad>
  );
}

const TableTitle = styled.div`
font-weight: 700;
font-size: 14px
line-height: 17px;
text-align: left;
text-transform: uppercase;
color: ${Colors.darkBrown};
margin-bottom: 12px;
margin-top: 40px;
`;

const Table = styled.div`
  border: 1px solid #fff;
  border-radius: 10px;
  overflow: hidden;
`;
const StyleTd = styled.td`
  width: 300px;
  border: 1px solid #fff;
  padding: 7px;
`;

const StyleP = styled.p`
  font-size: 14px;
  line-height: 17pxl
  color: #343434;
  text-align: center;
  margin-bottom: 43px;
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 20px;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  color: ${Colors.darkBrown};
`;
